/**
 * `useAccess()` — the single composable for paid-capability gating.
 *
 * Backed by the unified Pinia `useEntitlementsStore`, which mirrors
 * profiteia-backend's resolved AccessSet (see auth/access/resolver.py in the
 * backend repo). The backend in turn composes Stripe ProductFeature
 * lookup_keys with manual EntitlementGrant rows from scrumble (see
 * HasStripeEntitlements).
 *
 * Usage:
 *
 *   const { can, canAny, canAll, isSuperUser, isLoading, fetch, upgradeTo } = useAccess()
 *
 *   <button v-if="can('tool.imbalance-benchmark.use')" @click="run()">Run</button>
 *   <button v-else @click="upgradeTo('tool.imbalance-benchmark.use')">Upgrade</button>
 *
 * IMPORTANT: this composable is for *paid* gating. For deploy-time
 * kill switches (hide unreleased work in production, internal-only
 * rollout) use `useFeatureFlags` instead. The two are intentionally
 * independent: a paying customer should not see a feature that hasn't
 * been deployed, and an internal user should not bypass payment.
 */

import { computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useEntitlementsStore } from '@/stores/useEntitlementsStore';
import { goToUpgrade } from '@/utils/upgradeNavigation';

export function useAccess({ refreshOnMount = true } = {}) {
  const store = useEntitlementsStore();
  const { allowed, denied, isSuperUser, accessLoading, accessVersion, unknownKeys } =
    storeToRefs(store);

  if (refreshOnMount) {
    onMounted(() => {
      store.refreshAccessIfStale();
    });
  }

  /**
   * Reactive single-key check. Re-evaluates as soon as the access set
   * changes (e.g. after `fetchAccess` resolves).
   *
   * @param {string} key
   * @returns {import('vue').ComputedRef<boolean>}
   */
  const can = (key) => computed(() => store.can(key));

  /**
   * @param {string[]} keys
   * @returns {import('vue').ComputedRef<boolean>}
   */
  const canAny = (keys) => computed(() => store.canAny(keys));

  /**
   * @param {string[]} keys
   * @returns {import('vue').ComputedRef<boolean>}
   */
  const canAll = (keys) => computed(() => store.canAll(keys));

  return {
    can,
    canAny,
    canAll,
    allowed,
    denied,
    isSuperUser,
    isLoading: accessLoading,
    version: accessVersion,
    unknownKeys,

    /**
     * Force a fresh fetch of /api/v2/me/access. Idempotent — concurrent
     * calls share the same in-flight promise.
     */
    fetch: () => store.fetchAccess(),

    /**
     * Discard the cached set. Call from the logout flow.
     */
    clear: () => store.clearEntitlements(),

    /**
     * Trigger the upgrade flow for a missing entitlement.
     *
     * Phase 3e: accepts an optional `offer` argument carrying scrumble's
     * resolved BillingOffer (offer_type, stripe_price_id, stripe_product_id,
     * name, self_serve). The user is routed into the hash-router subscription
     * builder with enough context to preselect/highlight the right upgrade
     * path. Checkout is always confirmed from the builder.
     *
     * Backwards-compatible: callers without an offer get the original
     * Phase 1e behaviour.
     *
     * @param {string|null} missingKey
     * @param {{
     *   offer_type?: string,
     *   stripe_price_id?: string|null,
     *   stripe_product_id?: string|null,
     *   name?: string|null,
     *   self_serve?: boolean,
     * }|null} [offer]
     */
    upgradeTo: (missingKey, offer = null) => goToUpgrade(missingKey, offer),
  };
}
